import { useTranslation } from 'react-i18next'
import { getIn, useFormik } from 'formik'
import { MutableRefObject, useEffect } from 'react'
import { IconButton } from '@material-ui/core'
import { CloseOutlined } from '@material-ui/icons'
import { defaultValues } from 'components/forms/KnowledgeBaseContentFilesForm/utils'
import { KnowledgeBaseContentFilesFormFields } from 'components/forms/KnowledgeBaseContentFilesForm/types'
import { KnowledgeBaseContentFilesSchema } from 'validations/KnowledgeBaseContentFilesSchema'
import * as Styled from './KnowledgeBaseContentFilesForm.styles'

interface KnowledgeBaseContentFilesFormProps {
  initialValues?: KnowledgeBaseContentFilesFormFields
  onSave: (values: KnowledgeBaseContentFilesFormFields) => void
  submitRef: MutableRefObject<() => void>
  isLoading?: boolean
}

const KnowledgeBaseContentFilesForm: React.FC<KnowledgeBaseContentFilesFormProps> = ({
  initialValues,
  onSave: handleSave,
  submitRef,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  const submit = (values: KnowledgeBaseContentFilesFormFields) => {
    handleSave(values)
  }

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
  } = useFormik<KnowledgeBaseContentFilesFormFields>({
    initialValues: {
      ...defaultValues,
      ...initialValues,
    },
    validationSchema: KnowledgeBaseContentFilesSchema,
    onSubmit: submit,
    enableReinitialize: true,
  })

  useEffect(() => {
    if (submitRef?.current) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef?.current) {
        submitRef.current = () => {}
      }
    }
  }, [handleSubmit, submitRef])

  const handleFilesChange = (newFiles: File[]) => {
    setFieldValue('files', [...values.files, ...newFiles])
  }

  const handleRemoveFile = (fileIndex: number) => {
    setFieldValue('files', [...values.files.filter((_, index) => index !== fileIndex)])
  }

  return (
    <Styled.Form>
      <Styled.InfoSection
        label={t('common.visibility')}
        editValueComponent={
          <Styled.KnowledgeBaseVisibilitySelect
            id='visibility'
            name='visibility'
            label=''
            value={values.visibility}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={!!touched.visibility}
            errorMessage={t((errors.visibility as unknown as string) || '')}
            disabled={isLoading}
          />
        }
      />
      <Styled.InfoSection
        label={t('knowledgeBase.files')}
        editValueComponent={
          <Styled.UploadFilesButton
            onFilesChange={handleFilesChange}
            multiple
            disabled={isLoading}
          />
        }
      />
      {values.files.map((value, index) => {
        const errorMessage = getIn(errors, `files[${index}]`)

        return (
        // eslint-disable-next-line react/no-array-index-key
          <Styled.FilePreviewSection key={index}>
            <Styled.FilePreviewSectionRow>
              <p>{value.name}</p>
              <IconButton onClick={() => handleRemoveFile(index)} size='small' disabled={isLoading}>
                <CloseOutlined />
              </IconButton>
            </Styled.FilePreviewSectionRow>
            {errorMessage && (
              <Styled.FilePreviewSectionError>{t(errorMessage)}</Styled.FilePreviewSectionError>
            )}
          </Styled.FilePreviewSection>
        )
      })}

      <Styled.InfoSection
        label={t('common.description')}
        editValueComponent={
          <Styled.TextField
            id='description'
            name='description'
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.description}
            errorMessage={t(errors.description || '')}
            multiline
            minRows={4}
          />
        }
      />

    </Styled.Form>
  )
}

export { KnowledgeBaseContentFilesForm }
